import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import payrollDates from "./payrollDates";
import HourlyPayrollCalendar from "./HourlyPayrollCalendar";
import { Listbox } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import {
  fetchHourlyPayroll,
  createHourlyPayroll,
  updateHourlyPayroll,
  submitHourlyPayroll,
} from "../../store/thunks/hourlyPayrollThunk";

export default function HourlyPayrollPage() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const [currentDate] = useState(new Date());
  const [selectedPeriod, setSelectedPeriod] = useState(findCurrentPayPeriod());
  const [dailyHours, setDailyHours] = useState({});
  const [timeRemaining, setTimeRemaining] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isNewPayroll, setIsNewPayroll] = useState(false);
  const [payrollStatus, setPayrollStatus] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await dispatch(
          fetchHourlyPayroll({
            userId: user._id,
            payPeriod: selectedPeriod.payPeriodNumber,
          })
        );

        if (result?.payload) {
          // Handle when payroll exists
          setDailyHours(result.payload.hours || {});
          setPayrollStatus(result.payload.status || "");
          setIsNewPayroll(false); // Existing payroll entry found
        } else if (result.meta.response.status === 204) {
          // Handle 204 No Content response
          setDailyHours({});
          setPayrollStatus("");
          setIsNewPayroll(true); // No existing payroll entry
        } else {
          console.error("Unexpected response:", result);
          setDailyHours({});
          setPayrollStatus("");
          setIsNewPayroll(true);
        }
      } catch (error) {
        console.error("Error fetching payroll data:", error);
        setDailyHours({});
        setPayrollStatus("");
        setIsNewPayroll(true);
      }
    };

    fetchData();
  }, [dispatch, user._id, selectedPeriod]);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const submissionDate = new Date(
        Date.parse(selectedPeriod.submissionDate)
      );
      submissionDate.setHours(23, 59, 59, 999);

      const timeDiff = submissionDate - now;

      if (timeDiff > 0) {
        const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        );
        const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
        setTimeRemaining(`${days} days ${hours} hours ${minutes} minutes`);
      } else {
        setTimeRemaining("0 days 0 hours 0 minutes");
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [selectedPeriod]);

  function findCurrentPayPeriod() {
    let closestPeriod = payrollDates[0];
    payrollDates.forEach((period) => {
      const [startDateString, endDateString] = period.includeDates.split("-");
      const startDate = new Date(Date.parse(`${startDateString.trim()}`));
      const endDate = new Date(Date.parse(`${endDateString.trim()}`));
      endDate.setHours(23, 59, 59, 999);

      if (currentDate >= startDate && currentDate <= endDate) {
        closestPeriod = period;
      }
    });
    return closestPeriod;
  }
  const handleHoursChange = (date, type, value) => {
    const formattedDate = new Date(date).toISOString().split("T")[0];

    setDailyHours((prev) => ({
      ...prev,
      [formattedDate]: {
        ...prev[formattedDate],
        [type]:
          type === "regular" ? (value === "" ? "" : parseFloat(value)) : value,
      },
    }));
  };

  const handleCreatePayroll = async () => {
    const simplifiedDailyHours = transformKeysToDateFormat(dailyHours);
    try {
      await dispatch(
        createHourlyPayroll({
          userId: user._id,
          payPeriod: selectedPeriod.payPeriodNumber,
          hours: simplifiedDailyHours,
        })
      ).unwrap();

      setSuccessMessage("Payroll successfully created!");
      setTimeout(() => setSuccessMessage(""), 3000);
      setIsNewPayroll(false); // Payroll now exists
    } catch (error) {
      console.error("Failed to create payroll:", error);
      setSuccessMessage("Error: Could not create payroll.");
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  };

  const handleUpdatePayroll = async () => {
    const simplifiedDailyHours = transformKeysToDateFormat(dailyHours);
    try {
      await dispatch(
        updateHourlyPayroll({
          userId: user._id,
          payPeriod: selectedPeriod.payPeriodNumber,
          hours: simplifiedDailyHours,
          updatedBy: user._id,
        })
      ).unwrap();

      setSuccessMessage("Payroll successfully updated!");
      setTimeout(() => setSuccessMessage(""), 3000);

      // Refresh payroll data
      const refreshedData = await dispatch(
        fetchHourlyPayroll({
          userId: user._id,
          payPeriod: selectedPeriod.payPeriodNumber,
        })
      ).unwrap();

      setDailyHours(refreshedData.hours || {});
      setPayrollStatus(refreshedData.status || "");
    } catch (error) {
      console.error("Failed to update payroll:", error);
      setSuccessMessage("Error: Could not update payroll.");
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  };

  const handleSubmitPayroll = async () => {
    const confirmation = window.confirm(
      "Are you sure you want to submit this payroll? Once submitted, it cannot be edited."
    );
    if (!confirmation) return;

    const result = await dispatch(
      submitHourlyPayroll({
        userId: user._id,
        payPeriod: selectedPeriod.payPeriodNumber,
        updatedBy: user._id,
      })
    );
    if (submitHourlyPayroll.fulfilled.match(result)) {
      setSuccessMessage("Payroll submitted successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
      // Refresh payroll data
      const refreshedData = await dispatch(
        fetchHourlyPayroll({
          userId: user._id,
          payPeriod: selectedPeriod.payPeriodNumber,
        })
      ).unwrap();
      setDailyHours(refreshedData.hours || {});
      setPayrollStatus(refreshedData.status || ""); // Update status
    } else {
      console.error("Failed to submit payroll");
    }
  };

  const isSaveEnabled = () => {
    const submissionDate = new Date(Date.parse(selectedPeriod.submissionDate));
    submissionDate.setHours(23, 59, 59, 999);

    const now = new Date();
    return now <= submissionDate;
  };

  const isSubmitEnabled = () => {
    const submissionDate = new Date(Date.parse(selectedPeriod.submissionDate));
    submissionDate.setHours(23, 59, 59, 999);

    const now = new Date();
    return now <= submissionDate;
  };

  const transformKeysToDateFormat = (hours) => {
    const transformedHours = {};
    Object.keys(hours).forEach((key) => {
      const date = new Date(key).toISOString().split("T")[0];
      transformedHours[date] = hours[key];
    });
    return transformedHours;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="space-y-4">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">
            Hourly Payroll Entry
          </h1>
          <p className="text-lg text-gray-600">
            Currently viewing{" "}
            <span className="font-semibold text-gray-900">
              Pay Period{" "}
              {Number(String(selectedPeriod.payPeriodNumber).slice(4))} of{" "}
              {String(selectedPeriod.payPeriodNumber).slice(0, 4)}
            </span>
          </p>
        </div>

        {/* Pay Period Selector */}
        <div className="mt-6">
          <Listbox value={selectedPeriod} onChange={setSelectedPeriod}>
            <div className="relative">
              <Listbox.Button className="relative w-full cursor-pointer rounded-sm bg-white py-3 pl-4 pr-10 text-left border border-gray-200 shadow-sm hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                <span className="block truncate text-gray-900">
                  {selectedPeriod.includeDates}
                </span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  <ChevronUpDownIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </span>
              </Listbox.Button>
              <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-sm bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                {payrollDates.map((period) => (
                  <Listbox.Option
                    key={period.payPeriodNumber}
                    value={period}
                    className={({ active }) =>
                      `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                        active
                          ? "bg-indigo-50 text-indigo-900"
                          : "text-gray-900"
                      }`
                    }>
                    {({ selected }) => (
                      <>
                        <span
                          className={`block truncate ${
                            selected ? "font-medium" : "font-normal"
                          }`}>
                          {period.includeDates}
                        </span>
                        {selected && (
                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600">
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        )}
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </div>
          </Listbox>
        </div>

        {/* Calendar */}
        <div className="mt-8 bg-white rounded-sm shadow">
          <div className="p-6">
            <HourlyPayrollCalendar
              includeDates={selectedPeriod.includeDates}
              dailyHours={dailyHours}
              onHoursChange={handleHoursChange}
            />
          </div>
        </div>

        {/* Action Buttons and Status */}
        <div className="mt-8 space-y-4">
          {successMessage && (
            <div className="rounded-sm bg-green-50 p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <CheckIcon
                    className="h-5 w-5 text-green-400"
                    aria-hidden="true"
                  />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800">
                    {successMessage}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex-1">
              {payrollStatus === "Submitted" ? (
                <p className="text-sm text-green-600 font-medium">
                  Payroll has been successfully submitted. HR will process it at
                  the end of the pay period.
                </p>
              ) : (
                <div className="text-sm text-gray-600">
                  {isSaveEnabled() || isSubmitEnabled() ? (
                    <>
                      <p>
                        Updating and Submitting is permitted until{" "}
                        <span className="font-semibold">
                          {selectedPeriod.submissionDate}
                        </span>
                      </p>
                      <p className="mt-1">
                        Time remaining:{" "}
                        <span className="text-indigo-600 font-semibold">
                          {timeRemaining}
                        </span>
                      </p>
                    </>
                  ) : (
                    <p className="text-red-600 font-medium">
                      Submission period has ended. HR will process your payroll.
                    </p>
                  )}
                </div>
              )}
            </div>

            {payrollStatus !== "Submitted" && (
              <div className="flex gap-3">
                {isNewPayroll ? (
                  <button
                    onClick={handleCreatePayroll}
                    disabled={!isSaveEnabled()}
                    className={`inline-flex items-center px-4 py-2 rounded-sm text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                      isSaveEnabled()
                        ? "bg-indigo-600 text-white hover:bg-indigo-700"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                    }`}>
                    Create Payroll
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleSubmitPayroll}
                      disabled={!isSubmitEnabled()}
                      className={`inline-flex items-center px-4 py-2 rounded-sm text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${
                        isSubmitEnabled()
                          ? "bg-green-600 text-white hover:bg-green-700"
                          : "bg-gray-300 text-gray-500 cursor-not-allowed"
                      }`}>
                      Submit Payroll
                    </button>
                    <button
                      onClick={handleUpdatePayroll}
                      disabled={!isSaveEnabled()}
                      className={`inline-flex items-center px-4 py-2 rounded-sm text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                        isSaveEnabled()
                          ? "bg-indigo-600 text-white hover:bg-indigo-700"
                          : "bg-gray-300 text-gray-500 cursor-not-allowed"
                      }`}>
                      Update Payroll
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
